import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { OrganizationMembership } from '../services/organizationService';
import { invalidateTimezoneCache } from '@/utils/timezoneUtils';

interface OrganizationSettings {
  timezone: string;
  locale: string;
  notificationPreferences: Record<string, any>;
  integrationSettings: Record<string, any>;
  featureFlags: Record<string, any>;
}

interface OrganizationState {
  current: OrganizationMembership | null;
  available: OrganizationMembership[];
  settings: OrganizationSettings | null;
  isLoading: boolean;
}

const initialState: OrganizationState = {
  current: null,
  available: [],
  settings: null,
  isLoading: false,
};

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    setCurrentOrganization: (state, action: PayloadAction<OrganizationMembership>) => {
      state.current = action.payload;
      
      // Invalidate timezone cache when organization changes
      invalidateTimezoneCache();
    },
    setAvailableOrganizations: (state, action: PayloadAction<OrganizationMembership[]>) => {
      state.available = action.payload;
    },
    setOrganizationSettings: (state, action: PayloadAction<OrganizationSettings>) => {
      state.settings = action.payload;
      
      // Invalidate timezone cache when organization settings are loaded
      invalidateTimezoneCache();
    },
    updateOrganizationSettings: (state, action: PayloadAction<Partial<OrganizationSettings>>) => {
      if (state.settings) {
        state.settings = { ...state.settings, ...action.payload };
        
        // Invalidate timezone cache if timezone setting changed
        if (action.payload.timezone !== undefined) {
          invalidateTimezoneCache();
        }
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
  },
});

export const { 
  setCurrentOrganization, 
  setAvailableOrganizations, 
  setOrganizationSettings,
  updateOrganizationSettings,
  setLoading 
} = organizationSlice.actions;

export default organizationSlice.reducer;
